class BundleBuilder extends HTMLElement {
  constructor() {
    super();

    this.bundleName = `ks-bundle-${this.dataset.bundleId}`;
    this.bundleForms = this.querySelectorAll('.bundle-product-form');
    this.animationIndexFrame = null;
    this.requestProductAnimationIndexUpdate = this.requestProductAnimationIndexUpdate.bind(this);
    this.handleStickyCardMediaChange = this.handleStickyCardMediaChange.bind(this);
    this.handleStickyCardScroll = this.handleStickyCardScroll.bind(this);
    this.handleStickyCardResize = this.handleStickyCardResize.bind(this);
    this.handleMobileStickyCardUpdate = this.handleMobileStickyCardUpdate.bind(this);

    this.updateProductAnimationIndexes();
    this.handleProductForms();
    this.buildBundle();
    this.cardStickyPosition();
    this.stickyCardMobile();
    this.handleAddToCart();
    this.handleCheckout();
    window.addEventListener('resize', this.requestProductAnimationIndexUpdate);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.requestProductAnimationIndexUpdate);
    window.removeEventListener('resize', this.handleStickyCardResize);
    window.removeEventListener('scroll', this.handleStickyCardScroll);
    window.removeEventListener('scroll', this.handleMobileStickyCardUpdate);
    window.removeEventListener('resize', this.handleMobileStickyCardUpdate);

    if (this.desktopStickyQuery) {
      this.desktopStickyQuery.removeEventListener('change', this.handleStickyCardMediaChange);
    }

    if (this.animationIndexFrame) {
      window.cancelAnimationFrame(this.animationIndexFrame);
      this.animationIndexFrame = null;
    }

    if (this.stickyCardScrollFrame) {
      window.cancelAnimationFrame(this.stickyCardScrollFrame);
      this.stickyCardScrollFrame = null;
    }
  }

  getGridColumnCount(grid) {
    const columns = window.getComputedStyle(grid).gridTemplateColumns;

    if (!columns || columns === 'none') return 1;

    return Math.max(1, columns.split(' ').filter(Boolean).length);
  }

  updateProductAnimationIndexes() {
    this.querySelectorAll('.product-list').forEach((productList) => {
      const columnCount = this.getGridColumnCount(productList);

      productList.querySelectorAll('.product-item').forEach((item, index) => {
        item.style.setProperty('--animation-index', index % columnCount);
      });
    });
  }

  requestProductAnimationIndexUpdate() {
    if (this.animationIndexFrame) return;

    this.animationIndexFrame = window.requestAnimationFrame(() => {
      this.animationIndexFrame = null;
      this.updateProductAnimationIndexes();
    });
  }

  getBundleContents() {
    try {
      return JSON.parse(localStorage.getItem(this.bundleName)) || [];
    } catch (error) {
      console.error('Error parsing bundle contents:', error);
      return [];
    }
  }

  setBundleContents(contents) {
    localStorage.setItem(this.bundleName, JSON.stringify(contents));
  }

  bundleQuantity() {
    return this.getBundleContents().reduce((sum, item) => sum + Number(item.quantity), 0);
  }

  bundleTotalPrice() {
    return this.getBundleContents().reduce((sum, item) => sum + item.variant_price * item.quantity, 0);
  }

  escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  handleProductForms() {
    this.bundleForms.forEach((form) => {
      form.bundleVariants = this.getProductVariants(form);
      this.handleProductOptions(form);
      this.handleProductColorSwatches(form);
      this.updateSelectedProductVariant(form);

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.addToBundle(form);
      });
    });
  }

  getProductVariants(form) {
    const variantsScript = form.querySelector('script[type="application/json"][data-product-variants]');
    if (!variantsScript) return [];

    try {
      const variants = JSON.parse(variantsScript.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.error('Error parsing bundle product variants:', error);
      return [];
    }
  }

  handleProductOptions(form) {
    const productOptions = form.querySelector('.product-options');
    if (!productOptions) return;

    productOptions.querySelectorAll('select, input[data-option-position]').forEach((element) => {
      element.addEventListener('change', () => {
        this.updateSelectedProductVariant(form);
      });
    });
  }

  handleProductColorSwatches(form) {
    const article = form.closest('article');
    const colorSwatches = article?.querySelector('.product-card-color-swatches');
    if (!article || !colorSwatches) return;

    colorSwatches.querySelectorAll('input[data-option-position]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;

        this.syncProductOptionValue(form, input.dataset.optionPosition, input.value);
        this.updateProductImage(article, {
          image_400: input.dataset.variantImage400,
          image_800: input.dataset.variantImage800
        });
        this.updateSelectedProductVariant(form);
      });
    });

    colorSwatches.querySelectorAll('input[data-option-position]:checked').forEach((input) => {
      this.syncProductOptionValue(form, input.dataset.optionPosition, input.value);
    });
  }

  syncProductOptionValue(form, optionPosition, value) {
    if (!optionPosition || !value) return;

    form.querySelectorAll(`[data-option-position="${optionPosition}"]`).forEach((element) => {
      if (element.type === 'radio') {
        element.checked = element.value === value;
        return;
      }

      if (element.tagName === 'SELECT') {
        const option = Array.from(element.options).find((selectOption) => selectOption.value === value);
        if (option) {
          element.value = option.value;
        }
      }
    });
  }

  getSelectedOptions(form) {
    const productOptions = form.querySelector('.product-options');
    if (!productOptions) {
      return form.bundleVariants?.[0]?.options || [];
    }

    const selectedOptionsByPosition = {};

    productOptions.querySelectorAll('[data-option-position]').forEach((element) => {
      const position = Number(element.dataset.optionPosition);
      if (!Number.isFinite(position) || position < 1) return;
      if (element.type === 'radio' && !element.checked) return;
      if (element.tagName === 'SELECT' && element.value === '') return;

      selectedOptionsByPosition[position] = element.value;
    });

    return Object.keys(selectedOptionsByPosition)
      .sort((a, b) => Number(a) - Number(b))
      .map((position) => selectedOptionsByPosition[position]);
  }

  getSelectedProductVariant(form) {
    const selectedOptions = this.getSelectedOptions(form);
    const variants = form.bundleVariants || [];
    const variantOptionsCount = variants[0]?.options.length || 0;

    if (selectedOptions.length !== variantOptionsCount || !selectedOptions.every(Boolean)) {
      return null;
    }

    return variants.find((variant) => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    }) || null;
  }

  updateSelectedProductVariant(form) {
    const hiddenInput = form.querySelector('input[type="hidden"][name="id"]');
    const button = form.querySelector('button[type="submit"]');
    const buttonText = button?.querySelector('.btn-text') || button;
    const article = form.closest('article');
    const selectedVariant = this.getSelectedProductVariant(form);
    const selectedOptions = this.getSelectedOptions(form);
    const variants = form.bundleVariants || [];
    const hasSelectedAllOptions = selectedOptions.length === (variants[0]?.options.length || 0) && selectedOptions.every(Boolean);

    if (selectedVariant) {
      if (hiddenInput) hiddenInput.value = selectedVariant.id;
      form.dataset.selectedVariantAvailable = String(selectedVariant.available);
      form.dataset.selectedVariantTitle = selectedVariant.title;
      form.dataset.selectedVariantPrice = String(selectedVariant.price);
      form.dataset.selectedVariantImage = selectedVariant.image_800 || '';

      if (article) {
        this.updateProductImage(article, selectedVariant);
        this.updateProductPrice(article, selectedVariant);
        this.updateProductBadges(article, selectedVariant);
        this.updateProductLink(article, selectedVariant);
      }

      if (button && buttonText) {
        button.disabled = !selectedVariant.available;
        buttonText.textContent = selectedVariant.available
          ? this.dataset.textAddToBundle
          : window.theme.strings.product.soldOut;
      }
    } else if (hasSelectedAllOptions) {
      if (hiddenInput) hiddenInput.value = '';
      form.dataset.selectedVariantAvailable = 'false';

      if (button && buttonText) {
        button.disabled = true;
        buttonText.textContent = window.theme.strings.product.unavailable || 'Unavailable';
      }
    }

    this.handleOptionsAvailability(form);
    this.updateActions();
  }

  updateProductImage(article, variant) {
    const image = article.querySelector('.product-card-image img');
    if (!image) return;

    if (variant.image_800) {
      image.src = variant.image_800;
    }

    if (variant.image_400 && variant.image_800) {
      image.srcset = `${variant.image_400} 400w, ${variant.image_800} 800w`;
    }
  }

  updateProductPrice(article, variant) {
    const price = article.querySelector('.price');
    if (!price) return;

    price.querySelectorAll('[data-price]').forEach((element) => {
      element.textContent = window.Shopify.formatMoney(variant.price);
    });

    if (variant.compare_at_price > variant.price) {
      price.setAttribute('data-has-price-compare', 'true');
      price.querySelectorAll('[data-compare-price]').forEach((element) => {
        element.textContent = window.Shopify.formatMoney(variant.compare_at_price);
      });
    } else {
      price.setAttribute('data-has-price-compare', 'false');
      price.querySelectorAll('[data-compare-price]').forEach((element) => {
        element.textContent = '';
      });
    }
  }

  updateProductBadges(article, variant) {
    const soldOutBadge = article.querySelector('.badge-sold-out');
    const saleBadge = article.querySelector('.badge-sale');

    if (soldOutBadge) {
      soldOutBadge.hidden = variant.available;
    }

    if (!saleBadge) return;

    const saleBadgeType = saleBadge.getAttribute('data-sale-badge-type');
    if (variant.compare_at_price > variant.price) {
      const saleValue = variant.compare_at_price - variant.price;
      if (saleBadgeType === 'amount') {
        saleBadge.textContent = `${window.theme.strings.product.saleBadge} ${window.Shopify.formatMoney(saleValue)}`;
      } else if (saleBadgeType === 'percentage') {
        const percentage = Math.round((saleValue / variant.compare_at_price) * 100);
        saleBadge.textContent = `${window.theme.strings.product.saleBadge} ${percentage}%`;
      }
      saleBadge.hidden = !variant.available;
    } else {
      saleBadge.textContent = '';
      saleBadge.hidden = true;
    }
  }

  updateProductLink(article, variant) {
    const link = article.querySelector('.product-card-link');
    if (!link) return;

    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('variant', variant.id);
    link.href = url.toString();
  }

  getSelectedOptionsByPosition(form) {
    const selectedOptions = {};

    form.querySelectorAll('.product-options [data-option-position]').forEach((element) => {
      const position = Number(element.dataset.optionPosition);
      if (!Number.isFinite(position) || position < 1) return;
      if (element.type === 'radio' && !element.checked) return;
      if (element.tagName === 'SELECT' && element.value === '') return;

      selectedOptions[position] = element.value;
    });

    return selectedOptions;
  }

  isOptionValueAvailable(form, position, value, selectedOptions) {
    if (!value) return true;

    return (form.bundleVariants || []).some((variant) => {
      if (!variant.available) return false;

      for (let index = 1; index <= 3; index += 1) {
        const optionValue = index === position ? value : selectedOptions[index];
        if (!optionValue) continue;

        if (variant.options[index - 1] !== optionValue) {
          return false;
        }
      }

      return true;
    });
  }

  handleOptionsAvailability(form) {
    const productOptions = form.querySelector('.product-options');
    if (!productOptions) return;

    const selectedOptions = this.getSelectedOptionsByPosition(form);
    const article = form.closest('article');

    productOptions.querySelectorAll('input[data-option-position]').forEach((input) => {
      const position = Number(input.dataset.optionPosition);
      if (!Number.isFinite(position) || position < 1) return;

      const isAvailable = this.isOptionValueAvailable(form, position, input.value, selectedOptions);
      input.classList.toggle('disabled', !isAvailable);
      input.toggleAttribute('aria-disabled', !isAvailable);
    });

    productOptions.querySelectorAll('select[data-option-position]').forEach((select) => {
      const position = Number(select.dataset.optionPosition);
      if (!Number.isFinite(position) || position < 1) return;

      Array.from(select.options).forEach((option) => {
        if (option.value === '') return;

        const isAvailable = this.isOptionValueAvailable(form, position, option.value, selectedOptions);
        option.disabled = !isAvailable;
        option.classList.toggle('disabled', !isAvailable);
        option.toggleAttribute('aria-disabled', !isAvailable);
      });
    });

    article?.querySelectorAll('.product-card-color-swatches input[data-option-position]').forEach((input) => {
      const position = Number(input.dataset.optionPosition);
      if (!Number.isFinite(position) || position < 1) return;

      const isAvailable = this.isOptionValueAvailable(form, position, input.value, selectedOptions);
      input.classList.toggle('disabled', !isAvailable);
      input.toggleAttribute('aria-disabled', !isAvailable);
    });
  }

  addToBundle(form) {
    const button = form.querySelector('button[type="submit"]');
    const buttonText = button?.querySelector('.btn-text') || button;
    const selectedVariant = this.getSelectedProductVariant(form);

    if (!selectedVariant || !selectedVariant.available) return;

    let quantity = Number(form.querySelector('input[name="quantity"]')?.value || 1);
    const bundleMax = Number(this.dataset.bundleMax);
    const remainingQuantity = bundleMax > 0 ? bundleMax - this.bundleQuantity() : quantity;

    if (bundleMax > 0) {
      quantity = Math.min(quantity, Math.max(remainingQuantity, 0));
    }

    if (quantity <= 0) return;

    const bundleContents = this.getBundleContents();
    const existing = bundleContents.find((item) => item.variant_id === Number(selectedVariant.id));

    if (existing) {
      existing.quantity = Number(existing.quantity) + quantity;
    } else {
      bundleContents.push({
        product_id: Number(form.dataset.productId),
        product_handle: form.dataset.productHandle,
        product_title: form.dataset.productTitle,
        product_url: form.dataset.productUrl,
        variant_id: Number(selectedVariant.id),
        variant_image: selectedVariant.image_800,
        variant_price: Number(selectedVariant.price),
        variant_title: selectedVariant.title,
        quantity
      });
    }

    this.setBundleContents(bundleContents);
    this.buildBundle();

    if (!buttonText) return;

    buttonText.textContent = this.dataset.textAddedToBundle;
    window.setTimeout(() => {
      buttonText.textContent = this.dataset.textAddToBundle;
    }, 2000);
  }

  buildBundle() {
    this.updateProgress();
    this.updatePromoText();
    this.updateTotals();
    this.updateActions();
    this.updateItemCount();
    this.buildContentsModal();
  }

  updateProgress() {
    this.querySelectorAll('.bundle-builder-progress').forEach((progress) => {
      const maxTierValue = Number(this.dataset.maxTierValue);
      if (!maxTierValue) return;

      const percent = this.dataset.bundleMode === 'amount'
        ? Math.min(100, Math.round((this.bundleTotalPrice() / maxTierValue) * 100))
        : Math.min(100, Math.round((this.bundleQuantity() / maxTierValue) * 100));

      const progressBar = progress.querySelector('.bundle-builder-progress-bar');
      if (progressBar) {
        progressBar.style.width = `${percent}%`;
        progressBar.setAttribute('aria-valuenow', String(percent));
      }

      progress.querySelectorAll('li').forEach((tier) => {
        const left = Number(tier.style.left.replace('%', ''));
        tier.classList.toggle('active', left <= percent);
      });
    });
  }

  updatePromoText() {
    this.querySelectorAll('.bundle-builder-promo-text').forEach((element) => {
      const nextTier = this.getNextTier();
      let text = '';

      if (this.dataset.bundleMode === 'amount') {
        if (nextTier) {
          text = this.dataset.textPromoAmountHtml;
          text = text.replace('{{ amount }}', window.Shopify.formatMoney(nextTier.amount * 100 - this.bundleTotalPrice()));
          text = text.replace('{{ discount }}', `${nextTier.discount}%`);
          element.classList.remove('text-success');
        } else {
          text = this.dataset.textPromoFinalHtml;
          element.classList.add('text-success');
        }
      } else if (this.dataset.bundleMode === 'quantity') {
        if (nextTier) {
          text = this.dataset.textPromoQuantityHtml;
          text = text.replace('{{ count }}', nextTier.quantity - this.bundleQuantity());
          text = text.replace('{{ discount }}', `${nextTier.discount}%`);
          element.classList.remove('text-success');
        } else {
          text = this.dataset.textPromoFinalHtml;
          element.classList.add('text-success');
        }
      }

      element.innerHTML = text;
    });
  }

  updateTotals() {
    const discount = this.getTierDiscount();
    const totalPrice = this.bundleTotalPrice();
    const discountedPrice = totalPrice - totalPrice * (discount / 100);
    const savings = totalPrice - discountedPrice;

    this.querySelectorAll('[data-bundle-builder-subtotal]').forEach((element) => {
      if (discount > 0) {
        element.innerHTML = `<s>${window.Shopify.formatMoney(totalPrice)}</s><span>${window.Shopify.formatMoney(discountedPrice)}</span>`;
      } else {
        element.innerHTML = `<span>${window.Shopify.formatMoney(totalPrice)}</span>`;
      }
    });

    this.querySelectorAll('[data-bundle-builder-savings]').forEach((element) => {
      element.innerHTML = `<span>${window.Shopify.formatMoney(savings)}</span><span>(${discount}%)</span>`;
    });
  }

  updateActions() {
    const hasItems = this.getBundleContents().length > 0;
    const bundleMin = Number(this.dataset.bundleMin);
    const bundleMax = Number(this.dataset.bundleMax);
    const belowMin = bundleMin > 0 && this.bundleQuantity() < bundleMin;
    const atMax = bundleMax > 0 && this.bundleQuantity() >= bundleMax;

    this.querySelectorAll('[data-bundle-builder-btn-action]').forEach((button) => {
      button.disabled = !hasItems || belowMin;
    });

    this.bundleForms.forEach((form) => {
      const button = form.querySelector('button[type="submit"]');
      const productAvailable = form.dataset.productAvailable !== 'false';
      const selectedVariantAvailable = form.dataset.selectedVariantAvailable !== 'false';
      const disableOptions = atMax || !productAvailable;

      form.querySelectorAll('.product-options select, .product-options input[data-option-position]').forEach((element) => {
        element.disabled = disableOptions;
      });

      if (button) button.disabled = disableOptions || !selectedVariantAvailable;
    });
  }

  updateItemCount() {
    this.querySelectorAll('[data-bundle-builder-item-count]').forEach((element) => {
      element.textContent = this.bundleQuantity();
    });
  }

  buildContentsModal() {
    const modal = this.querySelector('.bundle-builder-contents-modal');
    if (!modal) return;

    const empty = modal.querySelector('.bundle-builder-contents-empty');
    const list = modal.querySelector('.bundle-builder-contents-list');
    const contents = this.getBundleContents();

    if (contents.length === 0) {
      empty.hidden = false;
      list.hidden = true;
      list.innerHTML = '';
      return;
    }

    empty.hidden = true;
    list.hidden = false;

    const aspectRatio = this.dataset.imgRatio || '1/1';
    const html = contents.slice().reverse().map((item) => {
      const variantId = Number(item.variant_id);
      if (!Number.isFinite(variantId)) return '';

      const variantTitle = item.variant_title || '';
      const hideVariantTitle = variantTitle.includes('Default');
      const image = this.escapeHTML(item.variant_image || '');
      const productUrl = this.escapeHTML(item.product_url || '');
      const productTitle = this.escapeHTML(item.product_title);
      const escapedVariantTitle = this.escapeHTML(variantTitle);

      return `
        <li class="bundle-builder-contents-item">
          <div class="bundle-builder-contents-item-inner">
            <a href="${productUrl}?variant=${variantId}" tabindex="-1">
              <div class="img-wrapper">
                <img
                  src="${image}"
                  alt=""
                  class="img-fluid"
                  width="800"
                  height="800"
                  style="aspect-ratio: ${aspectRatio};"
                  loading="lazy"
                >
              </div>
            </a>
            <div class="bundle-builder-contents-item-right">
              <div class="bundle-builder-contents-item-title-wrapper">
                <h3 class="heading h6">
                  <a href="${productUrl}?variant=${variantId}">${productTitle}</a>
                </h3>
                <p class="bundle-builder-contents-item-subtitle fs-sm">
                  <span ${hideVariantTitle ? 'hidden' : ''}>${escapedVariantTitle} - </span>
                  <span>${window.Shopify.formatMoney(item.variant_price * item.quantity)}</span>
                </p>
                <button
                  class="btn btn-icon btn-remove-from-bundle"
                  type="button"
                  data-variant-id="${variantId}"
                  aria-label="${this.dataset.textRemoveFromBundle}"
                >
                  <div class="animated-bin-icon"><span></span><span></span></div>
                </button>
              </div>
              <div class="bundle-builder-contents-item-qty-switcher-wrapper">
                <div class="qty-switcher" role="group" aria-label="${this.dataset.textQuantityLabel}">
                  <button class="btn" type="button" name="decrease" aria-label="${this.dataset.textQuantityDecrease}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  </button>
                  <input class="input" type="number" value="${item.quantity}" min="0" ${Number(this.dataset.bundleMax) > 0 ? `max="${this.dataset.bundleMax}"` : ''} aria-label="${this.dataset.textQuantityLabel}" data-variant-id="${item.variant_id}">
                  <button class="btn" type="button" name="increase" aria-label="${this.dataset.textQuantityIncrease}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </li>
      `;
    }).join('');

    list.innerHTML = html;
    this.bindContentsModalEvents(list);
  }

  bindContentsModalEvents(list) {
    list.querySelectorAll('.btn-remove-from-bundle').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const variantId = Number(button.dataset.variantId);
        const contents = this.getBundleContents().filter((item) => item.variant_id !== variantId);
        this.setBundleContents(contents);
        this.buildBundle();
      });
    });

    list.querySelectorAll('.qty-switcher').forEach((switcher) => {
      const input = switcher.querySelector('input');
      const decrease = switcher.querySelector('button[name="decrease"]');
      const increase = switcher.querySelector('button[name="increase"]');

      const commitQuantity = (value) => {
        const variantId = Number(input.dataset.variantId);
        let contents = this.getBundleContents();
        const item = contents.find((bundleItem) => bundleItem.variant_id === variantId);
        if (!item) return;

        const bundleMax = Number(this.dataset.bundleMax);
        const otherItemsQuantity = contents.reduce((sum, bundleItem) => {
          if (bundleItem.variant_id === variantId) return sum;
          return sum + Number(bundleItem.quantity);
        }, 0);
        const nextQuantity = bundleMax > 0 ? Math.min(value, Math.max(bundleMax - otherItemsQuantity, 0)) : value;

        if (nextQuantity > 0) {
          item.quantity = nextQuantity;
        } else {
          contents = contents.filter((bundleItem) => bundleItem.variant_id !== variantId);
        }

        this.setBundleContents(contents);
        this.buildBundle();
      };

      decrease.addEventListener('click', () => commitQuantity(Math.max(0, Number(input.value) - 1)));
      increase.addEventListener('click', () => commitQuantity(Number(input.value) + 1));
      input.addEventListener('change', () => commitQuantity(Math.max(0, Number(input.value))));
    });

    const bundleMax = Number(this.dataset.bundleMax);
    const atMax = bundleMax > 0 && this.bundleQuantity() >= bundleMax;

    this.querySelectorAll('.bundle-builder-contents-modal .qty-switcher button[name="increase"]').forEach((button) => {
      button.disabled = atMax;
    });
  }

  parseTiers() {
    const source = this.dataset.bundleMode === 'amount'
      ? this.dataset.bundleAmountTiers
      : this.dataset.bundleQuantityTiers;

    return source.split(';').map((tier) => {
      const [value, discount] = tier.split(':');
      if (this.dataset.bundleMode === 'amount') {
        return { amount: Number(value), discount: Number(discount) };
      }
      return { quantity: Number(value), discount: Number(discount) };
    }).filter((tier) => Number.isFinite(tier.discount));
  }

  getTierDiscount() {
    let discount = 0;

    this.parseTiers().forEach((tier) => {
      if (this.dataset.bundleMode === 'amount' && this.bundleTotalPrice() >= tier.amount * 100) {
        discount = tier.discount;
      } else if (this.dataset.bundleMode === 'quantity' && this.bundleQuantity() >= tier.quantity) {
        discount = tier.discount;
      }
    });

    return discount;
  }

  getNextTier() {
    return this.parseTiers().find((tier) => {
      if (this.dataset.bundleMode === 'amount') {
        return this.bundleTotalPrice() < tier.amount * 100;
      }
      return this.bundleQuantity() < tier.quantity;
    });
  }

  cardStickyPosition() {
    this.stickyCard = this.querySelector('.bundle-builder-card:not(.bundle-builder-sticky-card-mobile)');
    this.headerGroup = document.querySelector('#header-group');
    this.desktopStickyQuery = window.matchMedia('(min-width: 1200px)');
    this.stickyCardScrollFrame = null;

    if (!this.stickyCard) {
      return;
    }

    this.applyStickyCardPosition();
    this.desktopStickyQuery.addEventListener('change', this.handleStickyCardMediaChange);
    window.addEventListener('resize', this.handleStickyCardResize);
    window.addEventListener('scroll', this.handleStickyCardScroll, { passive: true });
  }

  getStickyCardTop() {
    const stickyOffset = 20;

    if (!this.headerGroup || this.headerGroup.classList.contains('hide')) {
      return stickyOffset;
    }

    return this.headerGroup.clientHeight + stickyOffset;
  }

  applyStickyCardPosition() {
    if (!this.stickyCard) return;

    if (!this.desktopStickyQuery || !this.desktopStickyQuery.matches) {
      this.stickyCard.style.top = '';
      return;
    }

    this.stickyCard.style.top = `${this.getStickyCardTop()}px`;
  }

  handleStickyCardMediaChange() {
    this.applyStickyCardPosition();
  }

  handleStickyCardResize() {
    this.applyStickyCardPosition();
  }

  handleStickyCardScroll() {
    if (!this.desktopStickyQuery || !this.desktopStickyQuery.matches) return;
    if (this.stickyCardScrollFrame) return;

    this.stickyCardScrollFrame = window.requestAnimationFrame(() => {
      this.stickyCardScrollFrame = null;
      this.applyStickyCardPosition();
    });
  }

  stickyCardMobile() {
    this.mainBundleCard = this.querySelector('.bundle-builder-card:not(.bundle-builder-sticky-card-mobile)');
    this.mobileStickyCard = this.querySelector('.bundle-builder-sticky-card-mobile');
    if (!this.mainBundleCard || !this.mobileStickyCard) return;

    this.handleMobileStickyCardUpdate();
    window.addEventListener('scroll', this.handleMobileStickyCardUpdate, { passive: true });
    window.addEventListener('resize', this.handleMobileStickyCardUpdate);
  }

  handleMobileStickyCardUpdate() {
    if (!this.mainBundleCard || !this.mobileStickyCard) return;

    const top = this.mainBundleCard.getBoundingClientRect().bottom + window.scrollY;
    const bottom = this.getBoundingClientRect().bottom + window.scrollY;
    this.mobileStickyCard.classList.toggle('show', window.scrollY > top && window.scrollY < bottom - window.innerHeight);
  }

  handleAddToCart() {
    this.querySelectorAll('[data-bundle-builder-btn-add-to-cart]').forEach((button) => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();

        const bundleContents = this.getBundleContents();
        if (bundleContents.length === 0) return;

        button.classList.add('loading');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        try {
          const items = bundleContents.map((item) => ({
            id: item.variant_id,
            quantity: item.quantity
          }));

          await window.ThemeCart.add({ items }, { returnFocusTarget: button });
          localStorage.removeItem(this.bundleName);
          this.buildBundle();
        } catch (error) {
          console.error('Error adding bundle to cart:', error);
        } finally {
          button.classList.remove('loading');
          button.disabled = false;
          button.removeAttribute('aria-busy');
          this.updateActions();
        }
      });
    });
  }

  handleCheckout() {
    this.querySelectorAll('[data-bundle-builder-btn-checkout]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const bundleContents = this.getBundleContents();
        if (bundleContents.length === 0) return;

        const items = bundleContents.map((item) => `${item.variant_id}:${item.quantity}`).join(',');
        localStorage.removeItem(this.bundleName);
        window.location.href = `${this.dataset.cartUrl}/${items}`;
      });
    });
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
